import {
    Button,
    DropDown,
    Pending,
    requiredRule,
    useErrorState,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { BOProps } from 'base-components/BOProps/BOProps';
import { getProps } from 'axios/propApi';
import useApi from 'hooks/useApi';
import { PropExtended, PropValue, SelectOption } from 'models/api/prop';
import { ReportTable } from 'models/api/report';
import { Dispatch, RefObject, SetStateAction } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { getEndOfTheDay, getStartOfTheDay } from 'utils/dataTime';
import GetOptions from 'utils/options';
import { ReportTypeForm } from './ReportChangeModal';
import { useReportWorkplaceState } from './Reports';

const ReportForm = ({
    reportTypes,
    toggle,
    modalRef,
}: {
    reportTypes: DropDown[];
    toggle: Dispatch<SetStateAction<boolean>>;
    modalRef?: RefObject<HTMLElement>;
}) => {
    const { t, i18n } = useTranslation();
    const api = useApi();
    const { handleRequestError } = useErrorState();
    const { watch } = useFormContext<ReportTypeForm>();
    const currentType = watch('type').toString(); // in fact watch returns string;
    const { selectedItem } = useReportWorkplaceState();
    const { append, remove } = useFieldArray<ReportTypeForm, 'props', 'id2'>({
        name: 'props',
        keyName: 'id2',
    });

    const { isFetching } = useQuery(
        ['ReportType', currentType],
        () => getProps(api, 'ReportType' + currentType, selectedItem?.id),
        {
            onSuccess: (props) => {
                const res = props.map<PropExtended>(({ selectOptions, values, ...prop }) => {
                    let tempSelectOptions: SelectOption[] | null = selectOptions;
                    if (prop.code === 'CultureCode' && tempSelectOptions) {
                        tempSelectOptions = tempSelectOptions.filter(
                            (i) => i.value === i18n.language
                        );
                    }

                    let tempValues: PropValue[] | null = values;
                    if (!selectedItem?.id) {
                        if (prop.code === 'Sd' && prop.type === 'datetime') {
                            tempValues = [{ id: null, value: getStartOfTheDay() }];
                        }

                        if (prop.code === 'Ed' && prop.type === 'datetime') {
                            tempValues = [{ id: null, value: getEndOfTheDay() }];
                        }

                        if (prop.code === 'NumberOfSolutions') {
                            tempValues = [{ id: null, value: '30' }];
                        }

                        if (tempSelectOptions?.length && !prop.moreThanOneFlag) {
                            tempValues = [{ id: null, value: tempSelectOptions[0].value }];
                        }
                    }

                    return {
                        ...prop,
                        selectOptions: tempSelectOptions,
                        values: tempValues,
                        container: modalRef,
                        disabled: !!selectedItem,
                    };
                });

                remove();
                append(res);
            },
            onError: (e) => handleRequestError(e),
        }
    );

    return (
        <>
            <ModalHeader toggle={() => toggle(false)}>
                <Trans i18nKey="BackOfficeReportParams" />
            </ModalHeader>
            <ModalBody className="custom-scrollbar">
                <ValidatedInput<ReportTable>
                    label={t('BackOfficeReportType')}
                    type="select"
                    name="type"
                    disabled={!!selectedItem}
                    rules={{ required: requiredRule }}
                    options={GetOptions(reportTypes)}
                />
                {!isFetching ? <BOProps<ReportTypeForm> name="props" /> : <Pending />}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={() => toggle(false)}>
                    <Trans i18nKey="BackOfficeCancel" />
                </Button>
                {!selectedItem && (
                    <Button type="submit" color="success" disabled={isFetching}>
                        <Trans i18nKey="BackOfficeSave" />
                    </Button>
                )}
            </ModalFooter>
        </>
    );
};

export default ReportForm;
